/**
 * React Hook for WebSocket Connection
 * 
 * Provides easy-to-use WebSocket functionality in React components
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { 
  getWebSocketService, 
  destroyWebSocketService,
  type ConnectionState,
  type SendMessagePayload,
  type UploadFilePayload 
} from '../services/websocketService';
import { TokenManager } from '../utils/auth';
import { env } from '../config/env';

export interface WebSocketMessage {
  id: string;
  user_id: string;
  sender: string;
  message: string;
  attachments: any[];
  is_read: boolean;
  created_at: string;
}

export interface TypingIndicator {
  user_id: string;
  user_name: string;
  is_typing: boolean;
  conversation_id: string;
}

export interface ReadReceipt {
  message_id: string;
  read_by: string;
  read_at: string;
}

export interface PresenceUpdate {
  user_id: string;
  status: 'online' | 'offline' | 'away';
}

export interface UseWebSocketOptions {
  autoConnect?: boolean;
  onMessage?: (message: WebSocketMessage) => void;
  onTyping?: (typing: TypingIndicator) => void;
  onReadReceipt?: (receipt: ReadReceipt) => void;
  onPresence?: (presence: PresenceUpdate) => void;
  onError?: (error: any) => void;
}

export function useWebSocket(options: UseWebSocketOptions = {}) {
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wsServiceRef = useRef<ReturnType<typeof getWebSocketService> | null>(null);
  const optionsRef = useRef(options);

  // Update options ref when they change
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // Initialize WebSocket service
  useEffect(() => {
    const token = TokenManager.getToken();
    
    if (!token) {
      console.log('[useWebSocket] No token available, skipping connection');
      return;
    }

    const wsUrl = env.WS_URL;
    
    try {
      wsServiceRef.current = getWebSocketService({
        url: wsUrl,
        token,
        reconnectInterval: 3000,
        maxReconnectAttempts: 10,
        heartbeatInterval: 30000,
      });

      // Setup event listeners
      const ws = wsServiceRef.current;

      const handleStateChange = (state: ConnectionState) => {
        setConnectionState(state);
        setIsConnected(state === 'connected');
      };

      const handleConnected = () => {
        console.log('[useWebSocket] Connected');
        setError(null);
      };

      const handleDisconnected = () => {
        console.log('[useWebSocket] Disconnected');
      };

      const handleMessage = (message: WebSocketMessage) => {
        optionsRef.current.onMessage?.(message);
      };

      const handleTyping = (typing: TypingIndicator) => {
        optionsRef.current.onTyping?.(typing);
      };

      const handleReadReceipt = (receipt: ReadReceipt) => {
        optionsRef.current.onReadReceipt?.(receipt);
      };

      const handlePresence = (presence: PresenceUpdate) => {
        optionsRef.current.onPresence?.(presence);
      };

      const handleError = (err: any) => {
        console.error('[useWebSocket] Error:', err);
        setError(err?.message || 'WebSocket error');
        optionsRef.current.onError?.(err);
      };

      const handleServerError = (err: { code: string; message: string }) => {
        console.error('[useWebSocket] Server error:', err);
        setError(err.message);
        optionsRef.current.onError?.(err);
      };

      const handleMaxReconnectAttempts = () => {
        setError('Failed to connect after multiple attempts');
      };

      // Register event listeners
      ws.on('state_change', handleStateChange);
      ws.on('connected', handleConnected);
      ws.on('disconnected', handleDisconnected);
      ws.on('message', handleMessage);
      ws.on('typing', handleTyping);
      ws.on('read_receipt', handleReadReceipt);
      ws.on('presence', handlePresence);
      ws.on('error', handleError);
      ws.on('server_error', handleServerError);
      ws.on('max_reconnect_attempts', handleMaxReconnectAttempts);

      // Auto-connect if enabled
      if (options.autoConnect !== false) {
        ws.connect();
      }

      // Cleanup
      return () => {
        ws.off('state_change', handleStateChange);
        ws.off('connected', handleConnected);
        ws.off('disconnected', handleDisconnected);
        ws.off('message', handleMessage);
        ws.off('typing', handleTyping);
        ws.off('read_receipt', handleReadReceipt);
        ws.off('presence', handlePresence);
        ws.off('error', handleError);
        ws.off('server_error', handleServerError);
        ws.off('max_reconnect_attempts', handleMaxReconnectAttempts);
        
        ws.disconnect();
        destroyWebSocketService();
      };
    } catch (err) {
      console.error('[useWebSocket] Initialization error:', err);
      setError(err instanceof Error ? err.message : 'Failed to initialize WebSocket');
    }
  }, [options.autoConnect]);

  // Send message
  const sendMessage = useCallback((payload: SendMessagePayload) => {
    if (!wsServiceRef.current) {
      console.error('[useWebSocket] Service not initialized');
      return;
    }
    wsServiceRef.current.sendMessage(payload);
  }, []);

  // Upload file
  const uploadFile = useCallback((payload: UploadFilePayload) => {
    if (!wsServiceRef.current) {
      console.error('[useWebSocket] Service not initialized');
      return;
    }
    wsServiceRef.current.uploadFile(payload);
  }, []);

  // Send typing indicator
  const sendTyping = useCallback((conversationId: string, isTyping: boolean) => {
    if (!wsServiceRef.current) return;
    
    if (isTyping) {
      wsServiceRef.current.sendTypingStart(conversationId);
    } else {
      wsServiceRef.current.sendTypingStop(conversationId);
    }
  }, []);

  // Mark message as read
  const markRead = useCallback((messageId: string) => {
    if (!wsServiceRef.current) return;
    wsServiceRef.current.markRead(messageId);
  }, []);

  // Manual connect
  const connect = useCallback(() => {
    if (!wsServiceRef.current) {
      console.error('[useWebSocket] Service not initialized');
      return;
    }
    wsServiceRef.current.connect();
  }, []);

  // Manual disconnect
  const disconnect = useCallback(() => {
    if (!wsServiceRef.current) return;
    wsServiceRef.current.disconnect();
  }, []);

  return {
    // State
    connectionState,
    isConnected,
    error,

    // Actions
    sendMessage,
    uploadFile,
    sendTyping,
    markRead,
    connect,
    disconnect,
  };
}
